import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { canManageSharePointSettings } from '../../lib/settingsPermissions';
import {
  fetchSharePointSettings,
  formatSharePointError,
  formatSharePointSiteInput,
  invokeSharePointEnquirySync,
  parseSharePointSiteInput,
  clearSharePointWorkbookPointers,
  saveSharePointSettings,
  sharePointPortalLabel,
  workbookNameMatchesPortal,
  type SharePointPortalKind,
  type SharePointSettings,
} from '../../lib/sharepointEnquiry';
import { logPortalActivityQuiet } from '../../lib/portalActivityLog';
import { usePostgresChangesRealtime } from '../../hooks/usePostgresChangesRealtime';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { Modal } from '../ui/Modal';
import { SharePointSyncToggle } from './SharePointSyncToggle';

import type { UserRole } from '../../types/crm';

type SharePointEnquiryPanelProps = {
  actorRole?: UserRole;
  portalKind?: SharePointPortalKind;
};

/** Create/refresh the current month's workbook and pull in incomplete projects. */
const CREATE_MONTH_FILE_LABEL = 'Create monthly file';

export function SharePointEnquiryPanel({
  actorRole: actorRoleProp,
  portalKind = 'crm',
}: SharePointEnquiryPanelProps = {}) {
  const { role: authRole } = useAuth();
  const actorRole = actorRoleProp ?? authRole;
  const canManage = canManageSharePointSettings(actorRole);
  const title = portalKind === 'it' ? 'IT Management' : 'Sales CRM';

  const [settings, setSettings] = useState<SharePointSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const editOpenRef = useRef(false);
  editOpenRef.current = editOpen;

  const [enabled, setEnabled] = useState(false);
  const [siteInput, setSiteInput] = useState('');
  const [folderPath, setFolderPath] = useState('General/02. Customer Enquiry');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const row = await fetchSharePointSettings(portalKind);
      setSettings(row);
      if (row) {
        if (!editOpenRef.current) {
          setEnabled(row.enabled);
          setSiteInput(formatSharePointSiteInput(row));
          setFolderPath(row.folder_path || 'General/02. Customer Enquiry');
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : formatSupabaseError(e));
    } finally {
      setLoading(false);
    }
  }, [portalKind]);

  useEffect(() => {
    void load();
  }, [load]);

  usePostgresChangesRealtime({
    enabled: true,
    channelName: `sharepoint-enquiry-live:${portalKind}`,
    portalKind,
    debounceMs: 500,
    onChange: () => void load(),
    listens: [{ schema: portalKind === 'it' ? 'it' : 'crm', table: 'crm_sharepoint_settings' }],
  });

  function openEdit() {
    if (settings) {
      setSiteInput(formatSharePointSiteInput(settings));
      setFolderPath(settings.folder_path || 'General/02. Customer Enquiry');
      setEnabled(settings.enabled);
    }
    setError(null);
    setMessage(null);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    if (settings) {
      setSiteInput(formatSharePointSiteInput(settings));
      setFolderPath(settings.folder_path || 'General/02. Customer Enquiry');
      setEnabled(settings.enabled);
    }
    setError(null);
    setMessage(null);
  }

  async function handleToggleEnabled(next: boolean) {
    if (!canManage || !settings?.site_hostname) {
      setError('Set site URL and folder in Edit before enabling sync.');
      return;
    }
    setToggling(true);
    setError(null);
    setMessage(null);
    setEnabled(next);
    try {
      const saved = await saveSharePointSettings(
        {
          enabled: next,
          site_hostname: settings.site_hostname,
          site_path: settings.site_path || '',
          folder_path: settings.folder_path || 'General/02. Customer Enquiry',
        },
        portalKind,
      );
      setSettings(saved);
      setMessage(next ? 'Sync turned on.' : 'Sync turned off.');
      logPortalActivityQuiet({
        portal: portalKind === 'it' ? 'it' : 'crm',
        action: 'update',
        entityType: 'sharepoint_settings',
        title: `SharePoint sync ${next ? 'enabled' : 'disabled'} (${sharePointPortalLabel(portalKind)})`,
        detail: `Enabled: ${next ? 'Yes' : 'No'}`,
        metadata: { portal_kind: portalKind },
      });
    } catch (err) {
      setEnabled(!next);
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setToggling(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canManage) return;

    const parsed = parseSharePointSiteInput(siteInput);
    if (!parsed.site_hostname || !parsed.site_path) {
      setError('Enter a valid SharePoint site URL (e.g. https://tenant.sharepoint.com/sites/Admin).');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await saveSharePointSettings(
        {
          enabled,
          site_hostname: parsed.site_hostname,
          site_path: parsed.site_path,
          folder_path: folderPath.trim() || 'General/02. Customer Enquiry',
        },
        portalKind,
      );
      setSettings(saved);
      setMessage('SharePoint settings saved.');
      setEditOpen(false);
      logPortalActivityQuiet({
        portal: portalKind === 'it' ? 'it' : 'crm',
        action: 'update',
        entityType: 'sharepoint_settings',
        title: `SharePoint settings updated (${sharePointPortalLabel(portalKind)})`,
        detail: [
          `Enabled: ${enabled ? 'Yes' : 'No'}`,
          `Site: ${parsed.site_hostname}${parsed.site_path}`,
          `Folder: ${folderPath.trim() || 'General/02. Customer Enquiry'}`,
        ].join('\n'),
        metadata: { portal_kind: portalKind },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDiagnose() {
    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await invokeSharePointEnquirySync('diagnose', portalKind);
      const steps = (result?.steps ?? {}) as Record<string, unknown>;
      const lines = Object.entries(steps).map(([key, value]) => {
        const text = typeof value === 'string' ? value : JSON.stringify(value, null, 0);
        return `${key}: ${text}`;
      });
      if (result?.ok) {
        setMessage(`Diagnose OK — ${lines.join(' · ')}`);
      } else {
        setError(`Diagnose found issues — ${lines.join(' · ')}`);
      }
      await load();
    } catch (err) {
      setError(formatSharePointError(err));
    } finally {
      setTesting(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setError(null);
    setMessage(null);
    try {
      const result = await invokeSharePointEnquirySync('test', portalKind);
      setMessage(
        `Connected to ${result?.siteName ?? 'SharePoint site'}${result?.folderUrl ? ` · folder OK` : ''}.`,
      );
    } catch (err) {
      setError(formatSharePointError(err));
    } finally {
      setTesting(false);
    }
  }

  async function handleSyncNow() {
    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      const result = await invokeSharePointEnquirySync('sync', portalKind);
      if (result?.skipped) {
        setMessage('Sync is disabled. Turn sync on to push projects to SharePoint.');
      } else {
        setMessage(
          `Synced ${result?.projectCount ?? 0} project(s) to ${result?.fileName ?? 'workbook'}.`,
        );
      }
      await load();
    } catch (err) {
      setError(formatSharePointError(err));
      await load();
    } finally {
      setSyncing(false);
    }
  }

  async function handleCreateMonthFile() {
    if (
      !window.confirm(
        portalKind === 'it'
          ? "Create or refresh this month's SharePoint workbook and import all incomplete projects?"
          : "Create or refresh this month's Customer Enquiry workbook and import all incomplete projects?",
      )
    ) {
      return;
    }

    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      if (
        settings?.current_workbook_name &&
        !workbookNameMatchesPortal(settings.current_workbook_name, portalKind)
      ) {
        await clearSharePointWorkbookPointers(portalKind);
      }

      const result = await invokeSharePointEnquirySync('monthly_rollover', portalKind);
      const fileName = String(result?.fileName ?? 'workbook');
      const portalUsed = String(result?.portal ?? '');
      const fnVersion = String(result?.functionVersion ?? '');

      if (portalKind === 'it' && !workbookNameMatchesPortal(fileName, 'it')) {
        setError(
          `Workbook is still named "${fileName}" (Sales). The server Edge Function must be redeployed — run: npx supabase functions deploy sharepoint-enquiry-sync`,
        );
      } else {
        let msg = `Monthly workbook ready: ${fileName} (${result?.projectCount ?? 0} projects).`;
        if (portalUsed && portalUsed !== portalKind) {
          msg += ` Warning: server used portal "${portalUsed}" instead of "${portalKind}".`;
        }
        if (!fnVersion) {
          msg += ' (Edge Function may be outdated — redeploy sharepoint-enquiry-sync.)';
        }
        setMessage(msg);
      }
      await load();
    } catch (err) {
      setError(formatSharePointError(err));
      await load();
    } finally {
      setSyncing(false);
    }
  }

  function formatStoredError(raw?: string | null): string {
    if (!raw) return '—';
    if (raw === '[object Object]') {
      return 'Previous sync failed. Select Test connection or Sync now to retry.';
    }
    return raw;
  }

  if (!canManage) {
    return null;
  }

  const siteDisplay = settings ? formatSharePointSiteInput(settings) : '';
  const folderDisplay = settings?.folder_path || '—';
  const busy = loading || saving || syncing || testing || toggling;

  return (
    <>
      <article className="sharepoint-config-row">
        <div className="sharepoint-config-row__main">
          <div className="sharepoint-config-row__identity">
            <h3 className="sharepoint-config-row__title">{title}</h3>
            {!loading ? (
              <p className="sharepoint-config-row__meta" title={siteDisplay || undefined}>
                {siteDisplay || 'No site configured'}
                {settings?.folder_path ? ` · ${folderDisplay}` : ''}
              </p>
            ) : (
              <p className="sharepoint-config-row__meta">Loading…</p>
            )}
          </div>

          <div className="sharepoint-config-row__status">
            <div>
              <span className="sharepoint-config-row__status-label">Last sync</span>
              <span className="sharepoint-config-row__status-value">
                {settings?.last_sync_at
                  ? formatSingaporeDateTime(settings.last_sync_at)
                  : '—'}
              </span>
            </div>
            <div className="sharepoint-config-row__status-wide">
              <span className="sharepoint-config-row__status-label">
                {settings?.last_error && !editOpen ? 'Error' : 'File'}
              </span>
              {settings?.last_error && !editOpen ? (
                <span
                  className="sharepoint-config-row__status-value sharepoint-config-row__status-value--error"
                  title={formatStoredError(settings.last_error)}
                >
                  {formatStoredError(settings.last_error)}
                </span>
              ) : (
                <span
                  className="sharepoint-config-row__status-value"
                  title={settings?.current_workbook_name ?? undefined}
                >
                  {settings?.current_workbook_name ?? '—'}
                </span>
              )}
            </div>
          </div>
        </div>

        {settings?.current_workbook_name &&
        !workbookNameMatchesPortal(settings.current_workbook_name, portalKind) ? (
          <div className="alert alert-warning sharepoint-config-row__alert">
            Stored file name looks like the other portal (
            <code>{settings.current_workbook_name}</code>). Use{' '}
            <strong>{CREATE_MONTH_FILE_LABEL}</strong> to create the correct{' '}
            {portalKind === 'it' ? 'IT' : 'Sales'} file.
          </div>
        ) : null}

        {!editOpen && error ? <div className="alert alert-error sharepoint-config-row__alert">{error}</div> : null}
        {!editOpen && message ? (
          <div className="alert alert-success sharepoint-config-row__alert">{message}</div>
        ) : null}

        <div className="sharepoint-config-row__actions">
          <SharePointSyncToggle
            variant="compact"
            checked={enabled}
            disabled={busy}
            onChange={(next) => void handleToggleEnabled(next)}
          />
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleTest()}
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleDiagnose()}
          >
            {testing ? 'Checking…' : 'Diagnose'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => void handleSyncNow()}
          >
            {syncing ? 'Working…' : 'Sync now'}
          </button>
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            title="Create or refresh this month's workbook and import incomplete projects"
            onClick={() => void handleCreateMonthFile()}
          >
            {CREATE_MONTH_FILE_LABEL}
          </button>
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={openEdit}>
            Edit
          </button>
        </div>
      </article>

      <Modal title={`Edit · ${title}`} open={editOpen} onClose={closeEdit} wide>
        <form className="settings-sharepoint-form settings-sharepoint-form--modal" onSubmit={(e) => void handleSave(e)}>
          {error ? <div className="alert alert-error">{error}</div> : null}
          {message ? <div className="alert alert-success">{message}</div> : null}

          <div className="settings-sharepoint-form__fields">
            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Site URL</span>
              <input
                className="field-input"
                placeholder="https://yourtenant.sharepoint.com/sites/Admin"
                value={siteInput}
                onChange={(e) => setSiteInput(e.target.value)}
                disabled={saving}
              />
            </label>

            <label className="field settings-sharepoint-form__field--wide">
              <span className="field-label">Folder path</span>
              <input
                className="field-input"
                placeholder="General/02. Customer Enquiry"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                disabled={saving}
              />
            </label>
          </div>

          <div className="settings-sharepoint-form__actions">
            <button type="submit" className="btn-primary" disabled={saving || testing}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || testing}
              onClick={() => void handleDiagnose()}
            >
              {testing ? 'Checking…' : 'Diagnose'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving || testing}
              onClick={() => void handleTest()}
            >
              {testing ? 'Testing…' : 'Test connection'}
            </button>
            <button type="button" className="btn-secondary" disabled={saving} onClick={closeEdit}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
